
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Upload, Save, RefreshCw, Settings2, Image as ImageIcon, Clock, Globe } from 'lucide-react';
import { useSiteSettings, SiteSettings } from '@/hooks/useSiteSettings';

const SiteSettingsManagement = () => {
  const { settings: currentSettings, loading: settingsLoading, refreshSettings } = useSiteSettings();
  const [settings, setSettings] = useState<SiteSettings>(currentSettings);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  // تحديث الإعدادات المحلية عند تغيير الإعدادات الحالية
  useEffect(() => {
    setSettings(currentSettings);
  }, [currentSettings]);

  const handleInputChange = (field: keyof SiteSettings, value: string | boolean) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      
      // التحقق من نوع الملف
      if (!file.type.startsWith('image/')) {
        throw new Error('يرجى رفع ملف صورة صحيح');
      }

      // التحقق من حجم الملف (أقل من 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('حجم الملف كبير جداً. يرجى رفع صورة أقل من 5MB');
      }
      
      // إنشاء اسم ملف فريد
      const fileExt = file.name.split('.').pop() || 'png';
      const fileName = `logo-${Date.now()}.${fileExt}`;
      
      console.log('Uploading file to site-logos bucket:', fileName, 'Size:', file.size, 'Type:', file.type);
      
      // حذف الشعار القديم أولاً إذا وجد
      if (settings.logo_url && settings.logo_url.includes('site-logos/')) {
        const oldFileName = settings.logo_url.split('/').pop();
        if (oldFileName) {
          await supabase.storage.from('site-logos').remove([oldFileName]);
        }
      }
      
      // رفع الصورة إلى Supabase Storage في bucket الصحيح
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('site-logos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw new Error(`فشل في رفع الملف: ${uploadError.message}`);
      }

      console.log('Upload successful:', uploadData);

      // الحصول على رابط الصورة العام
      const { data: { publicUrl } } = supabase.storage
        .from('site-logos')
        .getPublicUrl(fileName);

      console.log('Public URL:', publicUrl);

      setSettings(prev => ({
        ...prev,
        logo_url: publicUrl
      }));

      toast({
        title: "تم رفع الشعار",
        description: "تم رفع الشعار الجديد بنجاح"
      });
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      toast({
        title: "خطأ",
        description: error.message || "حدث خطأ في رفع الشعار",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setLoading(true);
      
      console.log('Saving settings:', settings);
      
      const { error } = await supabase
        .from('site_settings')
        .upsert({
          ...settings,
          updated_at: new Date().toISOString()
        }, { 
          onConflict: 'id',
          ignoreDuplicates: false 
        });
      
      if (error) {
        console.error('Save error:', error);
        throw error;
      }
      
      // تحديث الإعدادات في الـ hook
      await refreshSettings();
      
      toast({
        title: "تم الحفظ",
        description: "تم حفظ إعدادات الموقع بنجاح"
      });
    } catch (error: any) {
      console.error('Error saving settings:', error);
      toast({
        title: "خطأ",
        description: error.message || "حدث خطأ في حفظ الإعدادات",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-8 w-8 animate-spin" />
        <span className="mr-2">جاري تحميل الإعدادات...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">إعدادات الموقع</h2>
          <p className="text-gray-600 mt-1">تحكم في محتوى وإعدادات الموقع</p>
        </div>
        <Button onClick={saveSettings} disabled={loading}>
          {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          حفظ الإعدادات
        </Button>
      </div>

      {/* إعدادات الشعار */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            إعدادات الشعار
          </CardTitle>
          <CardDescription>رفع وتغيير شعار الموقع</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            {settings.logo_url && (
              <img 
                src={settings.logo_url} 
                alt="شعار الموقع" 
                className="h-16 w-auto object-contain border rounded"
                onError={(e) => {
                  console.error('Error loading logo:', e);
                  e.currentTarget.src = '/lovable-uploads/b93b5bad-cffe-4247-aa30-5fa8a0d5cc9a.png';
                }}
              />
            )}
            <div className="flex-1">
              <Label htmlFor="logo-upload">رفع شعار جديد</Label>
              <Input
                id="logo-upload"
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={uploading}
                className="mt-1"
              />
              {uploading && <p className="text-sm text-gray-500 mt-1">جاري الرفع...</p>}
              <p className="text-xs text-gray-500 mt-1">أنواع الملفات المدعومة: JPG, PNG, GIF (أقل من 5MB)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* إعدادات الصفحة الرئيسية */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            إعدادات الصفحة الرئيسية
          </CardTitle>
          <CardDescription>تعديل نصوص ومحتوى الصفحة الرئيسية</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="site-title">عنوان الموقع</Label>
            <Input
              id="site-title"
              value={settings.site_title}
              onChange={(e) => handleInputChange('site_title', e.target.value)}
              className="mt-1"
            />
          </div>
          
          <div>
            <Label htmlFor="hero-title">عنوان البانر الرئيسي</Label>
            <Input
              id="hero-title"
              value={settings.hero_title}
              onChange={(e) => handleInputChange('hero_title', e.target.value)}
              className="mt-1"
            />
          </div>
          
          <div>
            <Label htmlFor="hero-subtitle">العنوان الفرعي</Label>
            <Input
              id="hero-subtitle"
              value={settings.hero_subtitle}
              onChange={(e) => handleInputChange('hero_subtitle', e.target.value)}
              className="mt-1"
            />
          </div>
          
          <div>
            <Label htmlFor="hero-description">وصف البانر</Label>
            <Textarea
              id="hero-description"
              value={settings.hero_description}
              onChange={(e) => handleInputChange('hero_description', e.target.value)}
              className="mt-1"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* إعدادات صفحة عن كنف */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            إعدادات صفحة "عن كنف"
          </CardTitle>
          <CardDescription>تعديل محتوى صفحة التعريف بالشركة</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="about-title">عنوان الصفحة</Label>
            <Input
              id="about-title"
              value={settings.about_title}
              onChange={(e) => handleInputChange('about_title', e.target.value)}
              className="mt-1"
            />
          </div>
          
          <div>
            <Label htmlFor="about-content">المحتوى الرئيسي</Label>
            <Textarea
              id="about-content"
              value={settings.about_content}
              onChange={(e) => handleInputChange('about_content', e.target.value)}
              className="mt-1"
              rows={4}
            />
          </div>
          
          <div>
            <Label htmlFor="about-mission">المهمة</Label>
            <Textarea
              id="about-mission"
              value={settings.about_mission}
              onChange={(e) => handleInputChange('about_mission', e.target.value)}
              className="mt-1"
              rows={3}
            />
          </div>
          
          <div>
            <Label htmlFor="about-vision">الرؤية</Label>
            <Textarea
              id="about-vision"
              value={settings.about_vision}
              onChange={(e) => handleInputChange('about_vision', e.target.value)}
              className="mt-1"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* إعدادات التواصل */}
      <Card>
        <CardHeader>
          <CardTitle>معلومات التواصل</CardTitle>
          <CardDescription>تحديث معلومات التواصل الظاهرة في الموقع</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="contact-phone">رقم الهاتف</Label>
            <Input
              id="contact-phone"
              value={settings.contact_phone}
              onChange={(e) => handleInputChange('contact_phone', e.target.value)}
              className="mt-1"
              placeholder="+968xxxxxxxx"
            />
          </div>
          
          <div>
            <Label htmlFor="contact-email">البريد الإلكتروني</Label>
            <Input
              id="contact-email"
              type="email"
              value={settings.contact_email}
              onChange={(e) => handleInputChange('contact_email', e.target.value)}
              className="mt-1"
              placeholder="info@example.om"
            />
          </div>
          
          <div>
            <Label htmlFor="contact-address">العنوان</Label>
            <Input
              id="contact-address"
              value={settings.contact_address}
              onChange={(e) => handleInputChange('contact_address', e.target.value)}
              className="mt-1"
              placeholder="مسقط، سلطنة عمان"
            />
          </div>
        </CardContent>
      </Card>

      {/* إعدادات الصيانة */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            إعدادات الصيانة
          </CardTitle>
          <CardDescription>التحكم في أوقات صيانة الموقع</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            <Switch
              id="maintenance-mode"
              checked={settings.maintenance_mode}
              onCheckedChange={(checked) => handleInputChange('maintenance_mode', checked)}
            />
            <Label htmlFor="maintenance-mode">تفعيل وضع الصيانة</Label>
          </div>
          
          <div>
            <Label htmlFor="maintenance-message">رسالة الصيانة</Label>
            <Textarea
              id="maintenance-message"
              value={settings.maintenance_message}
              onChange={(e) => handleInputChange('maintenance_message', e.target.value)}
              className="mt-1"
              rows={3}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="maintenance-start">وقت بداية الصيانة</Label>
              <Input
                id="maintenance-start"
                type="datetime-local"
                value={settings.maintenance_start_time || ''}
                onChange={(e) => handleInputChange('maintenance_start_time', e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="maintenance-end">وقت انتهاء الصيانة</Label>
              <Input
                id="maintenance-end"
                type="datetime-local"
                value={settings.maintenance_end_time || ''}
                onChange={(e) => handleInputChange('maintenance_end_time', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SiteSettingsManagement;
